import React from "react";
import { Check, ExternalLink, X as XIcon } from "lucide-react";
import { performedServices, coordinatedServices, notPerformed } from "@/data/services";

export default function ServicesGrid() {
  return (
    <section id="what-lotwise-handles" className="relative bg-[#F7F7F5] text-brand-black" data-testid="services-grid">
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none dot-grid-fade" aria-hidden />
      <div className="relative mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6">
            <div className="eyebrow-accent">What LotWise Handles</div>
            <h2 className="mt-4 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.06]">
              The pieces that need to happen around a listing — coordinated as one managed process.
            </h2>
          </div>
          <p className="lg:col-span-6 text-[15.5px] leading-relaxed text-brand-gray max-w-xl">
            LotWise Home performs the media and preparation work directly, and coordinates the
            supporting pieces through approved providers where needed. The distinction is made clear
            below so you always know who is doing what.
          </p>
        </div>

        {/* Performed by LotWise */}
        <div className="mt-12 bg-white border border-brand-gray/25 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="h-6 w-6 rounded-full bg-gradient-to-br from-brand-blue to-brand-violet flex items-center justify-center">
              <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
            </span>
            <div>
              <div className="eyebrow-accent">Performed by LotWise</div>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight">Media, preparation, and coordination we deliver directly.</h3>
            </div>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {performedServices.map((s, i) => (
              <div key={s.title} className="border border-brand-gray/20 p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">Item {String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-3 text-[15px] font-bold tracking-tight">{s.title}</h4>
                <p className="mt-2 text-[13px] leading-relaxed text-brand-gray">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coordinated */}
        <div className="mt-6 bg-white border border-brand-gray/25 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="h-6 w-6 rounded-full border border-brand-black flex items-center justify-center">
              <ExternalLink className="h-3.5 w-3.5 text-brand-black" strokeWidth={2.5} />
            </span>
            <div>
              <div className="eyebrow text-brand-gray">Coordinated through approved providers</div>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight">Supporting work we arrange on your behalf.</h3>
            </div>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {coordinatedServices.map((s, i) => (
              <div key={s.title} className="border border-brand-gray/20 p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">Coordinated · {String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-3 text-[15px] font-bold tracking-tight">{s.title}</h4>
                <p className="mt-2 text-[13px] leading-relaxed text-brand-gray">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Not performed */}
        <div className="mt-6 bg-brand-black text-white p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="h-6 w-6 rounded-full border border-white/40 flex items-center justify-center">
              <XIcon className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </span>
            <div>
              <div className="eyebrow text-white/60">What LotWise does not do</div>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight">Kept out of scope so expectations stay honest.</h3>
            </div>
          </div>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {notPerformed.map((n) => (
              <li key={n} className="flex items-start gap-3 text-[14px] leading-relaxed text-white/85">
                <XIcon className="h-4 w-4 mt-0.5 text-white/60 shrink-0" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
